/**
 * dayCycle — pure, location-aware solar day-cycle + Islamic prayer-time engine.
 *
 * This module computes, for a given location + date, the six light-based day
 * parts (Early Morning → Late Night) bounded by real solar events, and the
 * five daily prayer times (Umm al-Qurā method) with their Adhan → Iqamah →
 * end timeline. It is the single source of truth consumed by the day clock,
 * the prayer-block auto-populate, and any future notifications.
 *
 * Design notes:
 * - PURE + side-effect free: no DOM, no rendering, no stores. Fully unit-tested.
 * - Solar instants are absolute (UTC) and timezone-independent; we convert to
 *   the LOCATION's local minute-of-day via its IANA time zone (DST-correct via
 *   Intl), per the brief — never a single stored offset.
 * - All tunables (Fajr angle, Isha interval, per-prayer wait/duration) are
 *   configurable; sensible Saudi/Umm al-Qurā defaults are shipped.
 *
 * Sun math is a small port of SunCalc (MIT, © Vladimir Agafonkin).
 */

export interface GeoLocation {
  lat: number;
  lng: number;
  /** IANA zone id, e.g. "Asia/Riyadh". Stored (not a fixed offset) for DST. */
  timeZone: string;
}

export type DayMode = 'normal' | 'white-nights' | 'midnight-sun' | 'polar-night';

export type DayPartKey =
  | 'earlyMorning' | 'morning' | 'afternoon' | 'evening' | 'night' | 'lateNight'
  | 'daylight' | 'whiteNight';

export interface DayPart {
  key: DayPartKey;
  name: string;
  /** Local minute-of-day [0,1440) of the part's start boundary. */
  startMinutes: number;
  /** Local minute-of-day (1..1440] of the part's end boundary. */
  endMinutes: number;
  durationMinutes: number;
}

export type SolarEventKey = 'dawn' | 'sunrise' | 'solarNoon' | 'goldenHour' | 'sunset' | 'dusk' | 'midnight';

export interface SolarEvent {
  key: SolarEventKey;
  /** Absolute instant, or null at high-latitude edge cases. */
  date: Date | null;
  /** Local minute-of-day [0,1440), or null when the event doesn't occur. */
  minutes: number | null;
}

export type PrayerKey = 'fajr' | 'dhuhr' | 'asr' | 'maghrib' | 'isha';

export interface PrayerConfig {
  key: PrayerKey;
  name: string;
  /** Adhan → Iqamah gap (minutes). */
  waitMin: number;
  /** Average congregational prayer length (minutes). */
  durationMin: number;
}

export interface PrayerTiming {
  key: PrayerKey;
  name: string;
  adhan: Date | null;
  iqamah: Date | null;
  end: Date | null;
  adhanMinutes: number | null;
  iqamahMinutes: number | null;
  endMinutes: number | null;
  /** Total Adhan→end span (waitMin + durationMin) — the prayer block duration. */
  blockMinutes: number;
  /** False when the prayer can't be computed (high-latitude depression edge). */
  available: boolean;
}

/** Optional voluntary (nafl) prayers derived from solar/obligatory times. */
export type VoluntaryPrayerKey = 'duha' | 'qiyam';

export interface VoluntaryPrayer {
  key: VoluntaryPrayerKey;
  name: string;
  /** Recommended start, local minute-of-day [0,1440), or null when unavailable. */
  startMinutes: number | null;
  /** Start + duration, local minute-of-day, or null. */
  endMinutes: number | null;
  /** Block length in minutes (= the configured duration). */
  blockMinutes: number;
  available: boolean;
}

/** Defaults for the voluntary-prayer block lengths (minutes). */
export const DEFAULT_DUHA_DURATION = 20;
export const DEFAULT_QIYAM_DURATION = 30;

export interface ComputeDayInput {
  location: GeoLocation;
  /** Any instant on the target calendar day (we resolve its local date). */
  date: Date;
  /** Include the prayer layer. Default false. */
  prayer?: boolean;
  /** Ramadan extends the Isha interval (90 → 120 min after Maghrib). */
  ramadan?: boolean;
  /** Override per-prayer wait/duration (e.g. from user settings). */
  prayerConfig?: PrayerConfig[];
  /** Fajr sun-depression angle (Umm al-Qurā = 18.5°). */
  fajrAngle?: number;
  /** Include Duha (forenoon) in `voluntary`. */
  duha?: boolean;
  /** Include Qiyam/Tahajjud (night) in `voluntary`. */
  qiyam?: boolean;
  /** Block lengths for the voluntary prayers (minutes). */
  duhaDurationMin?: number;
  qiyamDurationMin?: number;
  /**
   * Manual start overrides (local minute-of-day) for the voluntary prayers. When
   * set, the prayer starts at this exact time instead of its auto-derived time
   * (Duha = midway sunrise→noon; Qiyam = last third of the night) — and is always
   * `available` (a user-chosen time has no high-latitude edge case).
   */
  duhaStartMin?: number;
  qiyamStartMin?: number;
}

export interface DayCycle {
  mode: DayMode;
  parts: DayPart[];
  solarEvents: SolarEvent[];
  /** Null when prayer layer is off; entries may be unavailable at high latitude. */
  prayers: PrayerTiming[] | null;
  /** Requested voluntary prayers (Duha/Qiyam); empty when none requested. */
  voluntary: VoluntaryPrayer[];
}

/** Default per-prayer timings (Umm al-Qurā / typical Saudi congregational). */
export const DEFAULT_PRAYER_CONFIG: PrayerConfig[] = [
  { key: 'fajr', name: 'Fajr', waitMin: 25, durationMin: 10 },
  { key: 'dhuhr', name: 'Dhuhr', waitMin: 20, durationMin: 20 },
  { key: 'asr', name: 'Asr', waitMin: 20, durationMin: 20 },
  { key: 'maghrib', name: 'Maghrib', waitMin: 10, durationMin: 15 },
  { key: 'isha', name: 'Isha', waitMin: 20, durationMin: 20 },
];

export const DEFAULT_FAJR_ANGLE = 18.5;
const ISHA_AFTER_MAGHRIB = 90;
const ISHA_AFTER_MAGHRIB_RAMADAN = 120;

const PART_NAMES: Record<DayPartKey, string> = {
  earlyMorning: 'Early Morning',
  morning: 'Morning',
  afternoon: 'Afternoon',
  evening: 'Evening',
  night: 'Night',
  lateNight: 'Late Night',
  daylight: 'Daylight',
  whiteNight: 'White Night',
};

/* ----------------------------- SunCalc core ------------------------------ */
const PI = Math.PI;
const rad = PI / 180;
const dayMs = 864e5;
const J1970 = 2440588;
const J2000 = 2451545;
const e = rad * 23.4397;
const J0 = 0.0009;

const toJulian = (d: number) => d / dayMs - 0.5 + J1970;
const fromJulian = (j: number) => new Date((j + 0.5 - J1970) * dayMs);
const toDays = (d: number) => toJulian(d) - J2000;

const declination = (l: number, b: number) =>
  Math.asin(Math.sin(b) * Math.cos(e) + Math.cos(b) * Math.sin(e) * Math.sin(l));
const rightAscension = (l: number, b: number) =>
  Math.atan2(Math.sin(l) * Math.cos(e) - Math.tan(b) * Math.sin(e), Math.cos(l));
const solarMeanAnomaly = (d: number) => rad * (357.5291 + 0.98560028 * d);
const eclipticLongitude = (M: number) => {
  const C = rad * (1.9148 * Math.sin(M) + 0.02 * Math.sin(2 * M) + 0.0003 * Math.sin(3 * M));
  const P = rad * 102.9372;
  return M + C + P + PI;
};
const siderealTime = (d: number, lw: number) => rad * (280.16 + 360.9856235 * d) - lw;
const altitudeOf = (H: number, phi: number, dec: number) =>
  Math.asin(Math.sin(phi) * Math.sin(dec) + Math.cos(phi) * Math.cos(dec) * Math.cos(H));
const solarTransitJ = (ds: number, M: number, L: number) =>
  J2000 + ds + 0.0053 * Math.sin(M) - 0.0069 * Math.sin(2 * L);
const hourAngle = (h: number, phi: number, d: number) =>
  Math.acos((Math.sin(h) - Math.sin(phi) * Math.sin(d)) / (Math.cos(phi) * Math.cos(d)));

function sunCoords(d: number) {
  const M = solarMeanAnomaly(d);
  const L = eclipticLongitude(M);
  return { dec: declination(L, 0), ra: rightAscension(L, 0) };
}

function getAltitude(date: Date, lat: number, lng: number): number {
  const lw = rad * -lng;
  const phi = rad * lat;
  const d = toDays(date.valueOf());
  const c = sunCoords(d);
  const H = siderealTime(d, lw) - c.ra;
  return altitudeOf(H, phi, c.dec);
}

interface SunTimes {
  solarNoon: Date;
  sunrise: Date;
  sunset: Date;
  dawn: Date;
  dusk: Date;
  goldenHour: Date;
}

function getTimes(date: Date, lat: number, lng: number): SunTimes {
  const lw = rad * -lng;
  const phi = rad * lat;
  const d = toDays(date.valueOf());
  const n = Math.round(d - J0 - lw / (2 * PI));
  const ds = J0 + (0 + lw) / (2 * PI) + n;
  const M = solarMeanAnomaly(ds);
  const L = eclipticLongitude(M);
  const dec = declination(L, 0);
  const Jnoon = solarTransitJ(ds, M, L);

  const evt = (angle: number) => {
    const w = hourAngle(angle * rad, phi, dec);
    const a = J0 + (w + lw) / (2 * PI) + n;
    const Jset = solarTransitJ(a, M, L);
    const Jrise = Jnoon - (Jset - Jnoon);
    return { rise: fromJulian(Jrise), set: fromJulian(Jset) };
  };

  const s = evt(-0.833);
  const c = evt(-6);
  const g = evt(6);
  return {
    solarNoon: fromJulian(Jnoon),
    sunrise: s.rise,
    sunset: s.set,
    dawn: c.rise,
    dusk: c.set,
    goldenHour: g.set,
  };
}

/* --------------------------- prayer-time math ---------------------------- */
function getPrayerInstants(date: Date, lat: number, lng: number, fajrAngle: number, ishaInterval: number) {
  const lw = rad * -lng;
  const phi = rad * lat;
  const d = toDays(date.valueOf());
  const n = Math.round(d - J0 - lw / (2 * PI));
  const ds = J0 + (0 + lw) / (2 * PI) + n;
  const M = solarMeanAnomaly(ds);
  const L = eclipticLongitude(M);
  const dec = declination(L, 0);
  const Jnoon = solarTransitJ(ds, M, L);

  const J = (angleDeg: number, side: 'rise' | 'set'): number => {
    const w = hourAngle(angleDeg * rad, phi, dec);
    if (isNaN(w)) return NaN;
    const a = J0 + (w + lw) / (2 * PI) + n;
    const Jset = solarTransitJ(a, M, L);
    return side === 'rise' ? Jnoon - (Jset - Jnoon) : Jset;
  };

  // Asr: shadow factor 1 (majority / Saudi opinion).
  const asrAlt = Math.atan(1 / (1 + Math.tan(Math.abs(phi - dec)))) / rad;
  const fajrJ = J(-fajrAngle, 'rise');
  const asrJ = J(asrAlt, 'set');
  const maghJ = J(-0.833, 'set');
  const maghrib = isNaN(maghJ) ? null : fromJulian(maghJ);

  return {
    fajr: isNaN(fajrJ) ? null : fromJulian(fajrJ),
    dhuhr: fromJulian(Jnoon),
    asr: isNaN(asrJ) ? null : fromJulian(asrJ),
    maghrib,
    isha: maghrib ? new Date(maghrib.getTime() + ishaInterval * 60000) : null,
  };
}

/* ------------------------------- helpers --------------------------------- */
const mod = (x: number, m: number) => ((x % m) + m) % m;

/** Offset (minutes east of UTC) at `date` for an IANA zone — DST-correct. */
export function tzOffsetMinutes(date: Date, timeZone: string): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const map: Record<string, number> = {};
  for (const p of dtf.formatToParts(date)) {
    if (p.type !== 'literal') map[p.type] = Number(p.value);
  }
  const asUTC = Date.UTC(map.year, map.month - 1, map.day, map.hour, map.minute, map.second);
  return Math.round((asUTC - date.getTime()) / 60000);
}

/** Absolute instant → local minute-of-day [0,1440) at the location's zone. */
function localMinutes(date: Date | null, timeZone: string): number | null {
  if (!date || isNaN(date.getTime())) return null;
  const off = tzOffsetMinutes(date, timeZone);
  return mod(Math.round(date.getTime() / 60000) + off, 1440);
}

/** The local Y/M/D for an instant in a given zone (for anchoring the day). */
function localYMD(date: Date, timeZone: string): { y: number; m: number; d: number } {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
  });
  const map: Record<string, number> = {};
  for (const p of dtf.formatToParts(date)) {
    if (p.type !== 'literal') map[p.type] = Number(p.value);
  }
  return { y: map.year, m: map.month, d: map.day };
}

function part(key: DayPartKey, startMinutes: number, endMinutesRaw: number): DayPart {
  const endMinutes = endMinutesRaw <= startMinutes ? endMinutesRaw + 1440 : endMinutesRaw;
  return { key, name: PART_NAMES[key], startMinutes, endMinutes, durationMinutes: endMinutes - startMinutes };
}

/* ------------------------------ public API ------------------------------- */
export function computeDay(input: ComputeDayInput): DayCycle {
  const { location, date } = input;
  const { lat, lng, timeZone } = location;
  const fajrAngle = input.fajrAngle ?? DEFAULT_FAJR_ANGLE;

  // Anchor solar geometry on local noon of the target calendar date.
  const { y, m, d } = localYMD(date, timeZone);
  const anchor = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));

  const t = getTimes(anchor, lat, lng);
  const noonAlt = getAltitude(t.solarNoon, lat, lng);
  const sunriseBad = isNaN(t.sunrise.getTime());
  const sunsetBad = isNaN(t.sunset.getTime());
  const dawnBad = isNaN(t.dawn.getTime());
  const duskBad = isNaN(t.dusk.getTime());

  const solarEvents: SolarEvent[] = [
    { key: 'dawn', date: dawnBad ? null : t.dawn, minutes: localMinutes(dawnBad ? null : t.dawn, timeZone) },
    { key: 'sunrise', date: sunriseBad ? null : t.sunrise, minutes: localMinutes(sunriseBad ? null : t.sunrise, timeZone) },
    { key: 'solarNoon', date: t.solarNoon, minutes: localMinutes(t.solarNoon, timeZone) },
    { key: 'goldenHour', date: isNaN(t.goldenHour.getTime()) ? null : t.goldenHour, minutes: localMinutes(isNaN(t.goldenHour.getTime()) ? null : t.goldenHour, timeZone) },
    { key: 'sunset', date: sunsetBad ? null : t.sunset, minutes: localMinutes(sunsetBad ? null : t.sunset, timeZone) },
    { key: 'dusk', date: duskBad ? null : t.dusk, minutes: localMinutes(duskBad ? null : t.dusk, timeZone) },
    { key: 'midnight', date: null, minutes: 0 },
  ];

  let mode: DayMode;
  let parts: DayPart[];

  if (sunriseBad || sunsetBad) {
    // Polar: the sun never crosses the horizon this day.
    if (noonAlt > 0) {
      mode = 'midnight-sun';
      parts = [part('daylight', 0, 1440)];
    } else {
      mode = 'polar-night';
      parts = [part('lateNight', 0, 1440)];
    }
  } else {
    const sr = localMinutes(t.sunrise, timeZone)!;
    const noon = localMinutes(t.solarNoon, timeZone)!;
    const ss = localMinutes(t.sunset, timeZone)!;
    const golden = isNaN(t.goldenHour.getTime()) ? ss : localMinutes(t.goldenHour, timeZone)!;

    if (dawnBad || duskBad) {
      // White nights: sun rises & sets but no civil dark — no true Night/Late Night.
      mode = 'white-nights';
      parts = [
        part('morning', sr, noon),
        part('afternoon', noon, golden),
        part('evening', golden, ss),
        part('whiteNight', ss, sr),
      ];
    } else {
      mode = 'normal';
      const dawn = localMinutes(t.dawn, timeZone)!;
      const dusk = localMinutes(t.dusk, timeZone)!;
      parts = [
        part('earlyMorning', dawn, sr),
        part('morning', sr, noon),
        part('afternoon', noon, golden),
        part('evening', golden, dusk),
        part('night', dusk, 1440),
        part('lateNight', 0, dawn),
      ];
    }
  }

  let prayers: PrayerTiming[] | null = null;
  if (input.prayer) {
    const cfg = input.prayerConfig ?? DEFAULT_PRAYER_CONFIG;
    const ishaInterval = input.ramadan ? ISHA_AFTER_MAGHRIB_RAMADAN : ISHA_AFTER_MAGHRIB;
    const instants = getPrayerInstants(anchor, lat, lng, fajrAngle, ishaInterval);
    prayers = cfg.map((c) => {
      const adhan = instants[c.key];
      const valid = adhan instanceof Date && !isNaN(adhan.getTime());
      const iqamah = valid ? new Date(adhan!.getTime() + c.waitMin * 60000) : null;
      const end = valid ? new Date(adhan!.getTime() + (c.waitMin + c.durationMin) * 60000) : null;
      return {
        key: c.key,
        name: c.name,
        adhan: valid ? adhan : null,
        iqamah,
        end,
        adhanMinutes: localMinutes(valid ? adhan : null, timeZone),
        iqamahMinutes: localMinutes(iqamah, timeZone),
        endMinutes: localMinutes(end, timeZone),
        blockMinutes: c.waitMin + c.durationMin,
        available: valid,
      };
    });
  }

  // Voluntary (nafl) prayers, derived from solar + obligatory times.
  const voluntary: VoluntaryPrayer[] = [];
  if (input.duha || input.qiyam) {
    const minutesOf = (k: SolarEventKey) => solarEvents.find((e) => e.key === k)?.minutes ?? null;
    if (input.duha) {
      const dur = input.duhaDurationMin ?? DEFAULT_DUHA_DURATION;
      const ov = input.duhaStartMin;
      if (ov != null && Number.isFinite(ov)) {
        const start = mod(Math.round(ov), 1440); // user-chosen time wins
        voluntary.push({ key: 'duha', name: 'Duha', startMinutes: start, endMinutes: mod(start + dur, 1440), blockMinutes: dur, available: true });
      } else {
        const sr = minutesOf('sunrise');
        const noon = minutesOf('solarNoon');
        if (sr != null && noon != null && noon > sr) {
          const start = Math.round((sr + noon) / 2); // optimal: midway sunrise → solar noon
          voluntary.push({ key: 'duha', name: 'Duha', startMinutes: start, endMinutes: mod(start + dur, 1440), blockMinutes: dur, available: true });
        } else {
          voluntary.push({ key: 'duha', name: 'Duha', startMinutes: null, endMinutes: null, blockMinutes: dur, available: false });
        }
      }
    }
    if (input.qiyam) {
      const dur = input.qiyamDurationMin ?? DEFAULT_QIYAM_DURATION;
      const ov = input.qiyamStartMin;
      if (ov != null && Number.isFinite(ov)) {
        const start = mod(Math.round(ov), 1440); // user-chosen time wins
        voluntary.push({ key: 'qiyam', name: 'Qiyam', startMinutes: start, endMinutes: mod(start + dur, 1440), blockMinutes: dur, available: true });
      } else {
        const inst = getPrayerInstants(anchor, lat, lng, fajrAngle, ISHA_AFTER_MAGHRIB);
        const fajrMin = localMinutes(inst.fajr, timeZone);
        const maghribMin = localMinutes(inst.maghrib, timeZone);
        if (fajrMin != null && maghribMin != null) {
          const nightLen = mod(fajrMin - maghribMin, 1440); // Maghrib → next Fajr
          const start = mod(Math.round(fajrMin - nightLen / 3), 1440); // start of the last third
          voluntary.push({ key: 'qiyam', name: 'Qiyam', startMinutes: start, endMinutes: mod(start + dur, 1440), blockMinutes: dur, available: true });
        } else {
          voluntary.push({ key: 'qiyam', name: 'Qiyam', startMinutes: null, endMinutes: null, blockMinutes: dur, available: false });
        }
      }
    }
  }

  return { mode, parts, solarEvents, prayers, voluntary };
}

/** Convenience: the day part containing a given local minute-of-day. */
export function partAtMinute(parts: DayPart[], minuteOfDay: number): DayPart | null {
  const mm = mod(minuteOfDay, 1440);
  for (const p of parts) {
    const h = mm < p.startMinutes ? mm + 1440 : mm;
    if (h >= p.startMinutes && h < p.endMinutes) return p;
  }
  return parts[0] ?? null;
}
